import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import ListIcon from '@mui/icons-material/List';
import ViewListIcon from '@mui/icons-material/ViewList';
import { fetchData } from '../../helpers/apiUtils';
import { formatNumber } from '../../helpers/formatNumber';
import {
  CampaignHistoryRow,
  ScheduleHistoryRow,
  PlxScheduleStatus,
  PlxClient,
} from '../../helpers/plxMailingData';
import moment from 'moment';
import { germanNow } from '../../helpers/germanTime';

type ViewMode = 'campaigns' | 'schedules';

const HistoryTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('schedules');
  const [campaignData, setCampaignData] = useState<CampaignHistoryRow[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<PlxClient[]>([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState<PlxScheduleStatus[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [scheduleToCancel, setScheduleToCancel] = useState<ScheduleHistoryRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Retry dialog
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [scheduleToRetry, setScheduleToRetry] = useState<ScheduleHistoryRow | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryDatetime, setRetryDatetime] = useState<string>('');
  const [needsDatetimePicker, setNeedsDatetimePicker] = useState(false);

  // Load clients
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await fetchData<PlxClient[]>('/api/plx-mailings/clients');
        if (response.success && response.data) {
          setClients(response.data);
        }
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    loadClients();
  }, []);

  // Load history data
  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('view', viewMode);

      statusFilter.forEach((s) => params.append('status', s));
      clientFilter.forEach((c) => params.append('client_code', c));
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetchData<CampaignHistoryRow[] | ScheduleHistoryRow[]>(
        `/api/plx-mailings/history?${params.toString()}`,
      );

      if (response.success && response.data) {
        if (viewMode === 'campaigns') {
          setCampaignData(response.data as CampaignHistoryRow[]);
        } else {
          setScheduleData(response.data as ScheduleHistoryRow[]);
        }
      } else {
        setError('Fehler beim Laden der Daten');
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [viewMode, statusFilter, clientFilter, startDate, endDate]);

  const handleCancelClick = (schedule: ScheduleHistoryRow) => {
    setScheduleToCancel(schedule);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!scheduleToCancel) return;

    setCancelling(true);
    try {
      const response = await fetchData(
        `/api/plx-mailings/schedules/${scheduleToCancel.schedule_id}`,
        {
          method: 'DELETE',
        },
      );

      if (response.success) {
        setCancelDialogOpen(false);
        setScheduleToCancel(null);
        loadHistory();
      } else {
        setError('Fehler beim Abbrechen: ' + (response.error || 'Unbekannter Fehler'));
      }
    } catch (err) {
      setError('Fehler beim Abbrechen');
      console.error(err);
    } finally {
      setCancelling(false);
    }
  };

  // Retry handlers
  const handleRetryClick = (row: ScheduleHistoryRow) => {
    setScheduleToRetry(row);

    const originalDatetime = moment.utc(row.scheduled_datetime);
    const isPast = originalDatetime.isBefore(germanNow());

    setNeedsDatetimePicker(isPast);
    if (isPast) {
      const defaultNew = germanNow().add(15, 'minutes');
      const roundedMinutes = Math.ceil(defaultNew.minute() / 15) * 15;
      defaultNew.minute(roundedMinutes % 60).second(0);
      if (roundedMinutes >= 60) defaultNew.add(1, 'hour');
      setRetryDatetime(defaultNew.format('YYYY-MM-DDTHH:mm'));
    } else {
      setRetryDatetime(originalDatetime.format('YYYY-MM-DDTHH:mm'));
    }

    setRetryDialogOpen(true);
  };

  const handleRetryConfirm = async () => {
    if (!scheduleToRetry) return;
    setRetrying(true);
    try {
      const response = await fetchData(
        `/api/plx-mailings/schedules/${scheduleToRetry.schedule_id}/retry`,
        {
          method: 'POST',
          body: JSON.stringify({
            scheduled_datetime: needsDatetimePicker ? retryDatetime : undefined,
          }),
        },
      );
      if (response.success) {
        loadHistory();
        setRetryDialogOpen(false);
        setScheduleToRetry(null);
      } else {
        setError(response.error || 'Fehler beim erneuten Planen');
      }
    } catch (err) {
      setError('Fehler beim erneuten Planen');
      console.error(err);
    } finally {
      setRetrying(false);
    }
  };

  const getScheduleStatusChip = (status: PlxScheduleStatus) => {
    const config: Record<
      PlxScheduleStatus,
      { color: 'default' | 'warning' | 'success' | 'error'; label: string }
    > = {
      pending: { color: 'default', label: 'Ausstehend' },
      scheduled: { color: 'warning', label: 'Geplant' },
      sent: { color: 'success', label: 'Gesendet' },
      failed: { color: 'error', label: 'Fehlgeschlagen' },
      cancelled: { color: 'default', label: 'Abgebrochen' },
    };
    const { color, label } = config[status];
    return (
      <Chip
        size="small"
        label={label}
        color={color}
        sx={status === 'cancelled' ? { textDecoration: 'line-through' } : {}}
      />
    );
  };

  const getClientChip = (clientCode: string, espType: string) => {
    const colors: Record<string, string> = {
      ft: '#4caf50',
      be: '#2196f3',
      bg: '#ff9800',
      sw: '#9c27b0',
      tt: '#f44336',
      kme: '#00838f',
      dnv: '#5d4037',
    };
    return (
      <Chip
        size="small"
        label={`${clientCode.toUpperCase()} (${espType})`}
        sx={{ backgroundColor: colors[clientCode] || '#9e9e9e', color: 'white' }}
      />
    );
  };

  // Render schedule view
  const renderScheduleTable = () => (
    <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              ID
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Kampagne
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Produkt
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Client
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Betreff
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Geplant
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Status
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Empfänger
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Aktionen
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scheduleData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                Keine Daten gefunden
              </TableCell>
            </TableRow>
          ) : (
            scheduleData.map((row) => (
              <TableRow key={row.schedule_id} hover>
                <TableCell>{row.schedule_id}</TableCell>
                <TableCell>{row.campaign_name}</TableCell>
                <TableCell>
                  {row.product_name && (
                    <Typography variant="body2">
                      {row.product_name}
                      {row.product_code && ` (${row.product_code})`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{getClientChip(row.client_code, row.esp_type)}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {row.is_non_opener && (
                      <Chip
                        size="small"
                        label="Non-Opener"
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    )}
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {row.email_subject}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {moment.utc(row.scheduled_datetime).format('DD.MM.YYYY HH:mm')}
                </TableCell>
                <TableCell>{getScheduleStatusChip(row.status)}</TableCell>
                <TableCell>
                  {row.metrics?.recipients ? formatNumber(row.metrics.recipients, 0) : '-'}
                </TableCell>
                <TableCell>
                  {row.can_cancel && (
                    <Tooltip title="Abbrechen">
                      <IconButton size="small" color="error" onClick={() => handleCancelClick(row)}>
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.status === 'failed' && !row.is_non_opener && (
                    <Tooltip title="Erneut planen">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleRetryClick(row)}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render campaign view
  const renderCampaignTable = () => (
    <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              ID
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Name
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Produkt
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Status
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              E-Mail
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Schedules
            </TableCell>
            <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
              Erstellt
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {campaignData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center">
                Keine Daten gefunden
              </TableCell>
            </TableRow>
          ) : (
            campaignData.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  {row.product_name && (
                    <Typography variant="body2">
                      {row.product_name}
                      {row.product_code && ` (${row.product_code})`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.campaign_status}
                    color={row.campaign_status === 'sent' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {row.has_email ? (
                    <Chip size="small" label="Vorhanden" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Fehlt" color="warning" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    {(row.schedule_stats?.pending || 0) > 0 && (
                      <Chip size="small" label={`${row.schedule_stats.pending} ausstehend`} />
                    )}
                    {(row.schedule_stats?.scheduled || 0) > 0 && (
                      <Chip
                        size="small"
                        label={`${row.schedule_stats.scheduled} geplant`}
                        color="warning"
                      />
                    )}
                    {(row.schedule_stats?.sent || 0) > 0 && (
                      <Chip
                        size="small"
                        label={`${row.schedule_stats.sent} gesendet`}
                        color="success"
                      />
                    )}
                    {(row.schedule_stats?.failed || 0) > 0 && (
                      <Chip
                        size="small"
                        label={`${row.schedule_stats.failed} fehlgeschlagen`}
                        color="error"
                      />
                    )}
                    {(row.non_opener_sent_count || 0) > 0 && (
                      <Chip
                        size="small"
                        label={`${row.non_opener_sent_count} Non-Opener`}
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{moment(row.created_at).format('DD.MM.YYYY')}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* View Mode Toggle and Filters */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={2} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="schedules">
              <ViewListIcon sx={{ mr: 0.5 }} /> Schedules
            </ToggleButton>
            <ToggleButton value="campaigns">
              <ListIcon sx={{ mr: 0.5 }} /> Kampagnen
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              label="Status"
              onChange={(e: SelectChangeEvent<PlxScheduleStatus[]>) =>
                setStatusFilter(e.target.value as PlxScheduleStatus[])
              }
              renderValue={(selected) => selected.join(', ')}
            >
              <MenuItem value="pending">Ausstehend</MenuItem>
              <MenuItem value="scheduled">Geplant</MenuItem>
              <MenuItem value="sent">Gesendet</MenuItem>
              <MenuItem value="failed">Fehlgeschlagen</MenuItem>
              <MenuItem value="cancelled">Abgebrochen</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Client</InputLabel>
            <Select
              multiple
              value={clientFilter}
              label="Client"
              onChange={(e: SelectChangeEvent<string[]>) =>
                setClientFilter(e.target.value as string[])
              }
              renderValue={(selected) => selected.map((s) => s.toUpperCase()).join(', ')}
            >
              {clients.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name} ({c.esp_type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={2}>
          <TextField
            type="date"
            size="small"
            label="Von"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            size="small"
            label="Bis"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'schedules' ? (
        renderScheduleTable()
      ) : (
        renderCampaignTable()
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Schedule abbrechen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie diesen Schedule wirklich abbrechen?
            {scheduleToCancel && (
              <Box mt={1}>
                <strong>Kampagne:</strong> {scheduleToCancel.campaign_name}
                <br />
                <strong>Client:</strong> {scheduleToCancel.client_code.toUpperCase()}
                <br />
                <strong>Geplant:</strong>{' '}
                {moment.utc(scheduleToCancel.scheduled_datetime).format('DD.MM.YYYY HH:mm')}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Nein</Button>
          <Button onClick={handleCancelConfirm} color="error" disabled={cancelling}>
            {cancelling ? <CircularProgress size={24} /> : 'Ja, abbrechen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retry Dialog */}
      <Dialog open={retryDialogOpen} onClose={() => setRetryDialogOpen(false)}>
        <DialogTitle>Schedule erneut planen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie diesen fehlgeschlagenen Schedule erneut planen?
          </DialogContentText>
          {needsDatetimePicker && (
            <TextField
              fullWidth
              type="datetime-local"
              label="Neuer Zeitpunkt"
              value={retryDatetime}
              onChange={(e) => setRetryDatetime(e.target.value)}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleRetryConfirm} color="primary" disabled={retrying}>
            {retrying ? <CircularProgress size={24} /> : 'Erneut planen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryTab;
